/**
 * Flashcard
 *
 * A component for displaying interactive flashcards in chat messages
 *
 * Primary responsibilities:
 * - Display a question/answer pair as a real 3D flippable card
 * - Provide animation for flipping between question and answer sides
 * - Allow tracking of user confidence with the flashcard content
 * - Support touch/swipe gestures for mobile users
 */

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  IconCards,
  IconArrowsShuffle,
  IconCheck,
  IconX,
  IconFlipHorizontal
} from '@tabler/icons-react'
import { motion } from 'framer-motion'
import { Streamdown } from 'streamdown'
import { streamdownStaticProps } from '@/components/messages/streamdown-animation'
import { streamdownMathPlugins } from '@/components/messages/streamdown-math'

export type ConfidenceLevel = 'correct' | 'incorrect' | null

interface FlashcardProps {
  id?: string
  question: string
  answer: string
  className?: string
  initialConfidence?: ConfidenceLevel
  onConfidenceChange?: (confidence: ConfidenceLevel) => void
  forceInitialFlip?: boolean
  onFlipChange?: (isFlipped: boolean) => void
}

// Chat scroll is vertical. The old handler flipped on vertical swipe (>50px),
// so scrolling the message flipped every card the finger started on — the whole
// deck ended up on Answer mid-animation and stacked via preserve-3d.
const TAP_MOVE_PX = 12
const HORIZONTAL_FLIP_PX = 60
const SUPPRESS_CLICK_MS = 450

export const Flashcard: React.FC<FlashcardProps> = ({
  id = `flashcard-${Math.random().toString(36).substr(2, 9)}`,
  question,
  answer,
  className,
  initialConfidence = null,
  onConfidenceChange,
  forceInitialFlip = false,
  onFlipChange
}) => {
  const [isFlipped, setIsFlipped] = useState(forceInitialFlip)
  const [confidence, setConfidence] = useState<ConfidenceLevel>(initialConfidence)
  const cardRef = useRef<HTMLDivElement>(null)
  const isFlippedRef = useRef(isFlipped)
  const onFlipChangeRef = useRef(onFlipChange)
  const touchStartRef = useRef<{ x: number; y: number } | null>(null)
  const suppressClickUntilRef = useRef(0)

  useEffect(() => {
    isFlippedRef.current = isFlipped
  }, [isFlipped])

  useEffect(() => {
    onFlipChangeRef.current = onFlipChange
  }, [onFlipChange])

  const applyFlip = useCallback((next: boolean) => {
    isFlippedRef.current = next
    setIsFlipped(next)
    onFlipChangeRef.current?.(next)
  }, [])

  const toggleFlip = useCallback(() => {
    applyFlip(!isFlippedRef.current)
  }, [applyFlip])

  const flipCard = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    // Button-driven flips should not be cancelled by a trailing synthetic click.
    suppressClickUntilRef.current = Date.now() + SUPPRESS_CLICK_MS
    toggleFlip()
  }

  const handleConfidenceClick = (level: ConfidenceLevel, e: React.MouseEvent) => {
    e.stopPropagation() // Prevent card flip
    suppressClickUntilRef.current = Date.now() + SUPPRESS_CLICK_MS

    setConfidence(level)
    onConfidenceChange?.(level)

    // Always show answer when user marks confidence
    if (!isFlippedRef.current) {
      applyFlip(true)
    }
  }

  const resetCard = (e: React.MouseEvent) => {
    e.stopPropagation() // Prevent card flip
    suppressClickUntilRef.current = Date.now() + SUPPRESS_CLICK_MS
    applyFlip(false)
    setConfidence(null)
    onConfidenceChange?.(null)
  }

  const isInteractiveTarget = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return false
    return Boolean(target.closest('button, a, input, textarea, select, [role="button"]'))
  }

  // Tap or sideways swipe flips. Vertical movement is chat scroll — never flip.
  useEffect(() => {
    const card = cardRef.current
    if (!card) return

    const handleTouchStart = (e: TouchEvent) => {
      touchStartRef.current = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      const start = touchStartRef.current
      if (!start) return
      const dx = e.touches[0].clientX - start.x
      const dy = e.touches[0].clientY - start.y
      // Once clearly vertical, abandon flip for this gesture (scroll owns it).
      if (Math.abs(dy) > TAP_MOVE_PX && Math.abs(dy) >= Math.abs(dx)) {
        touchStartRef.current = null
        suppressClickUntilRef.current = Date.now() + SUPPRESS_CLICK_MS
      }
    }

    const handleTouchCancel = () => {
      touchStartRef.current = null
      suppressClickUntilRef.current = Date.now() + SUPPRESS_CLICK_MS
    }

    const handleTouchEnd = (e: TouchEvent) => {
      const start = touchStartRef.current
      touchStartRef.current = null
      if (!start) return
      if (isInteractiveTarget(e.target)) return

      const dx = e.changedTouches[0].clientX - start.x
      const dy = e.changedTouches[0].clientY - start.y
      const absX = Math.abs(dx)
      const absY = Math.abs(dy)

      // Always eat the synthetic click that follows touch on iOS/WebKit.
      suppressClickUntilRef.current = Date.now() + SUPPRESS_CLICK_MS

      // Vertical / scroll-ish gesture — do not flip.
      if (absY >= absX && absY > TAP_MOVE_PX) return

      // Clear sideways swipe.
      if (absX > HORIZONTAL_FLIP_PX && absX > absY) {
        toggleFlip()
        return
      }

      // Stationary (or near-stationary) tap.
      if (absX <= TAP_MOVE_PX && absY <= TAP_MOVE_PX) {
        toggleFlip()
      }
    }

    const handleClick = (e: MouseEvent) => {
      if (Date.now() < suppressClickUntilRef.current) {
        e.preventDefault()
        e.stopPropagation()
        return
      }
      if (isInteractiveTarget(e.target)) return
      toggleFlip()
    }

    card.addEventListener('touchstart', handleTouchStart, { passive: true })
    card.addEventListener('touchmove', handleTouchMove, { passive: true })
    card.addEventListener('touchend', handleTouchEnd)
    card.addEventListener('touchcancel', handleTouchCancel)
    card.addEventListener('click', handleClick)

    return () => {
      card.removeEventListener('touchstart', handleTouchStart)
      card.removeEventListener('touchmove', handleTouchMove)
      card.removeEventListener('touchend', handleTouchEnd)
      card.removeEventListener('touchcancel', handleTouchCancel)
      card.removeEventListener('click', handleClick)
    }
  }, [toggleFlip])

  // Update local state if initialConfidence prop changes
  useEffect(() => {
    if (initialConfidence !== undefined) {
      setConfidence(initialConfidence)
    }
  }, [initialConfidence])

  // Update local state if forceInitialFlip prop changes
  useEffect(() => {
    setIsFlipped(forceInitialFlip)
  }, [forceInitialFlip])

  const faceShell = (side: 'front' | 'back') =>
    cn(
      'flashcard-face absolute inset-0 rounded-lg border p-0 shadow-sm',
      side === 'front' ? 'flashcard-face-front' : 'flashcard-face-back',
      confidence === 'correct'
        ? 'border-green-400 bg-green-50 dark:bg-green-900/10'
        : confidence === 'incorrect'
          ? 'border-red-400 bg-red-50 dark:bg-red-900/10'
          : 'border-border bg-card'
    )

  return (
    // Opacity-only entrance. Any transform (y/scale/translateZ) on an ancestor
    // flattens preserve-3d on iOS WebKit — Answer paints over Question and
    // backfaces leak as mirrored ghosts in the chat scroll.
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      className={cn('flashcard-root isolate my-6', className)}
      data-flashcard-id={id}
    >
      <div className="flashcard-perspective">
        <div
          ref={cardRef}
          className={cn(
            'flashcard-rotator relative h-[240px] w-full cursor-pointer rounded-lg md:h-[280px]',
            isFlipped && 'is-flipped',
            confidence === 'correct'
              ? 'card-border-correct'
              : confidence === 'incorrect'
                ? 'card-border-incorrect'
                : 'card-border-default'
          )}
        >
          {/* Front side (Question) */}
          <div className={faceShell('front')}>
            <div className="flex items-center border-b p-3">
              <div className="flex items-center gap-2">
                <IconCards size={16} className="text-muted-foreground" />
                <h3 className="text-sm font-medium">Question</h3>
              </div>
            </div>

            <div className="flex h-[calc(100%-48px)] flex-col justify-between">
              {/* m-auto (not items/justify-center): centered flex content taller
                  than this scroll box clips its top beyond scroll reach, so
                  long questions were unreadable. Auto margins center short
                  content and collapse to 0 when it overflows. */}
              <div className="flex flex-1 overflow-y-auto whitespace-pre-wrap text-xl text-blue-700 dark:text-blue-300">
                <Streamdown
                  {...streamdownStaticProps}
                  className="m-auto whitespace-pre-wrap p-4 text-center"
                  plugins={streamdownMathPlugins}
                >
                  {question}
                </Streamdown>
              </div>

              <div className="flex items-center justify-between border-t p-2">
                {!confidence && (
                  <span className="text-muted-foreground text-xs">
                    Tap or swipe sideways to flip
                  </span>
                )}

                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button variant="outline" size="sm" onClick={flipCard} className="ml-auto text-xs">
                    <IconFlipHorizontal size={14} className="mr-1" />
                    Flip Card
                  </Button>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Back side (Answer) — static rotateY(180). Parent .is-flipped adds
              another 180 so this face points at the camera. */}
          <div className={faceShell('back')}>
            <div className="flex items-center border-b p-3">
              <div className="flex items-center gap-2">
                <IconCards size={16} className="text-muted-foreground" />
                <h3 className="text-sm font-medium">Answer</h3>
              </div>
            </div>

            <div className="flex h-[calc(100%-48px)] flex-col justify-between">
              {/* Same m-auto scroll-reach fix as the front face — long
                  explanations/answers must stay fully scrollable. */}
              <div className="flex flex-1 overflow-y-auto whitespace-pre-wrap text-xl text-amber-700 dark:text-amber-300">
                <Streamdown
                  {...streamdownStaticProps}
                  className="m-auto whitespace-pre-wrap p-4 text-center"
                  plugins={streamdownMathPlugins}
                >
                  {answer}
                </Streamdown>
              </div>

              <div className="border-t p-2">
                {!confidence ? (
                  <div className="flex flex-col items-center gap-2 sm:flex-row">
                    <span className="text-muted-foreground mb-2 text-xs sm:mb-0">
                      How well did you know this?
                    </span>
                    <div className="ml-auto flex items-center gap-2">
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => handleConfidenceClick('incorrect', e)}
                          className="border-red-200 bg-red-100 text-xs text-red-700 hover:bg-red-200 dark:border-red-800/30 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
                        >
                          <IconX size={14} className="mr-1" />
                          Missed
                        </Button>
                      </motion.div>
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => handleConfidenceClick('correct', e)}
                          className="border-green-200 bg-green-100 text-xs text-green-700 hover:bg-green-200 dark:border-green-800/30 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30"
                        >
                          <IconCheck size={14} className="mr-1" />
                          Got it
                        </Button>
                      </motion.div>
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Button variant="outline" size="sm" onClick={flipCard} className="text-xs">
                          <IconFlipHorizontal size={14} className="mr-1" />
                          Flip
                        </Button>
                      </motion.div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-end gap-2">
                    {confidence && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2, duration: 0.3 }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Button variant="outline" size="sm" onClick={resetCard} className="text-xs">
                          <IconArrowsShuffle size={14} className="mr-1" />
                          Reset
                        </Button>
                      </motion.div>
                    )}
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button variant="outline" size="sm" onClick={flipCard} className="text-xs">
                        <IconFlipHorizontal size={14} className="mr-1" />
                        Flip Card
                      </Button>
                    </motion.div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx global>{`
        .flashcard-perspective {
          perspective: 1000px;
          -webkit-perspective: 1000px;
        }

        .flashcard-rotator {
          transform-style: preserve-3d;
          -webkit-transform-style: preserve-3d;
          transition: transform 0.55s ease-in-out;
          transform: rotateY(0deg);
          -webkit-transform: rotateY(0deg);
        }

        .flashcard-rotator.is-flipped {
          transform: rotateY(180deg);
          -webkit-transform: rotateY(180deg);
        }

        .flashcard-face {
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }

        .flashcard-face-front {
          transform: rotateY(0deg);
          -webkit-transform: rotateY(0deg);
        }

        /* Required: without this, Answer faces the camera at rest and paints
           over Question (later in DOM). Flip then shows mirrored leftovers. */
        .flashcard-face-back {
          transform: rotateY(180deg);
          -webkit-transform: rotateY(180deg);
        }

        .card-border-correct {
          box-shadow:
            0 4px 6px -1px rgba(74, 222, 128, 0.1),
            0 2px 4px -1px rgba(74, 222, 128, 0.06);
        }

        .card-border-incorrect {
          box-shadow:
            0 4px 6px -1px rgba(248, 113, 113, 0.1),
            0 2px 4px -1px rgba(248, 113, 113, 0.06);
        }

        .card-border-default {
          box-shadow:
            0 4px 6px -1px rgba(0, 0, 0, 0.1),
            0 2px 4px -1px rgba(0, 0, 0, 0.06);
        }

        @media (prefers-reduced-motion: reduce) {
          .flashcard-rotator {
            transition: none !important;
          }
        }
      `}</style>
    </motion.div>
  )
}
